import React, { useState, useEffect, useRef } from 'react';

function LogoSplash({ onComplete }) {
  const [showVideo, setShowVideo] = useState(true);
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const videoRef = useRef(null);
  const hasPlayedRef = useRef(false);

  const handleVideoEnd = () => {
    if (hasPlayedRef.current) return; // Prevent multiple calls
    
    hasPlayedRef.current = true;
    
    // Mark video as shown in localStorage so it won't show again
    localStorage.setItem('logo_splash_shown', 'true');
    
    // Hide video and call onComplete
    setShowVideo(false);
    if (onComplete) {
      onComplete();
    }
  };

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (video && needsInteraction) {
      video.muted = false; // Ensure unmuted when user clicks
      video.play()
        .then(() => {
          setNeedsInteraction(false);
        })
        .catch(err => {
          console.warn('Video play failed:', err);
          // If play still fails, mark as shown and proceed to login
          localStorage.setItem('logo_splash_shown', 'true');
          handleVideoEnd();
        });
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video is loaded before playing
    const attemptPlay = () => {
      // Try playing with sound first
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Autoplay succeeded
            setNeedsInteraction(false);
          })
          .catch(() => {
            // Autoplay failed, try muted autoplay
            video.muted = true;
            video.play()
              .then(() => {
                // Muted autoplay succeeded, unmute after a short delay
                setTimeout(() => {
                  video.muted = false;
                }, 100);
                setNeedsInteraction(false);
              })
              .catch(() => {
                // Both failed, require user interaction
                setNeedsInteraction(true);
              });
          });
      }
    };

    // Maximum duration fallback - always proceed after video duration + buffer
    const setupMaxDuration = () => {
      if (video.duration && video.duration > 0) {
        // Use actual video duration + 2 second buffer
        const maxTime = (video.duration * 1000) + 2000;
        return setTimeout(() => {
          if (!hasPlayedRef.current) {
            console.warn('Maximum duration reached, proceeding to login');
            // Mark as shown even if video didn't complete
            localStorage.setItem('logo_splash_shown', 'true');
            handleVideoEnd();
          }
        }, maxTime);
      } else {
        // Fallback: 15 seconds if duration unknown
        return setTimeout(() => {
          if (!hasPlayedRef.current) {
            console.warn('Maximum duration reached, proceeding to login');
            // Mark as shown even if video didn't complete
            localStorage.setItem('logo_splash_shown', 'true');
            handleVideoEnd();
          }
        }, 15000);
      }
    };

    let maxDurationTimeout = setupMaxDuration();

    // Update max duration when video metadata loads
    const onDurationChange = () => {
      if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
      maxDurationTimeout = setupMaxDuration();
    };
    video.addEventListener('durationchange', onDurationChange);

    // Wait for video to be ready
    if (video.readyState >= 2) {
      // Video is already loaded
      attemptPlay();
    } else {
      // Wait for video to load
      const onLoadedData = () => {
        attemptPlay();
        // Update max duration now that we have metadata
        if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
        maxDurationTimeout = setupMaxDuration();
      };
      video.addEventListener('loadeddata', onLoadedData, { once: true });
      video.load(); // Ensure video starts loading

      return () => {
        if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('durationchange', onDurationChange);
      };
    }

    return () => {
      if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
      video.removeEventListener('durationchange', onDurationChange);
    };
  }, []);

  const handleVideoError = () => {
    console.warn('Logo video failed to load, proceeding to login');
    // Mark as shown even on error so it doesn't keep trying
    localStorage.setItem('logo_splash_shown', 'true');
    handleVideoEnd();
  };

  if (!showVideo) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        cursor: needsInteraction ? 'pointer' : 'default',
      }}
      onClick={handleVideoClick}
    >
      <video
        ref={videoRef}
        src="/animations/company_logo.mp4"
        onEnded={handleVideoEnd}
        onError={handleVideoError}
        onLoadedData={() => {
          // Ensure video tries to play when loaded
          const video = videoRef.current;
          if (video && video.paused && !needsInteraction) {
            video.play().catch(() => {
              // If still fails, try muted
              video.muted = true;
              video.play().then(() => {
                setTimeout(() => { video.muted = false; }, 100);
              }).catch(() => {
                setNeedsInteraction(true);
              });
            });
          }
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90%',
          maxHeight: '90%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          marginTop: '-80px',
          marginLeft: '40px',
        }}
        playsInline
        muted={false}
        loop={false}
        autoPlay
        preload="auto"
      />
    </div>
  );
}

export default LogoSplash;
